import React, { useRef, useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import {
  Button,
  Autocomplete,
  Trash as TrashIcon,
  IconButton,
  Tooltip
  // @ts-ignore
} from '@netbasequid/core'
import { useTranslation } from 'react-i18next'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import SpinningQ from 'components/share/SpinningQ'
import { useAppSelector } from 'app/hooks'
import { selectScheduledReportSuggestedUserList } from 'features/suggestedUsers/selectors'
import { useFormContext } from 'react-hook-form'
import { Theme } from 'features/ui/types'
import { ScrollbarStyle } from 'styles'

import { ScheduledReportSuggestedUser } from './types'

const TextFieldContainer = styled.div`
  display: flex;
  gap: 8px;
`

const SubscriberList = styled.div<{
  $theme: SystemTheme
  $isLoading?: boolean
  $mode: Theme
}>`
  border: 1px solid ${({ $theme }) => $theme.interaction.border.default};
  border-radius: 2px;
  margin-top: 8px;
  height: 160px;
  overflow-y: auto;
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
    `}
  ${ScrollbarStyle}
`

const StyledSubscriber = styled.div<{ $theme: SystemTheme }>`
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 34px;
  gap: 16px;
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  font-weight: ${({ $theme }) => $theme.fontWeight.regular};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  border-bottom: 1px solid ${({ $theme }) => $theme.common.divider[100]};
  & > button {
    display: none;
  }

  &:hover {
    background-color: ${({ $theme }) => $theme.interaction.onElevation.default};
    & > button {
      display: inline-flex;
    }
  }
`

const Subscriber = ({
  children,
  onDelete
}: {
  children: React.ReactNode
  onDelete?: () => void
}) => {
  const mode = useDisplayTheme()
  const theme = useSysTheme()
  return (
    <StyledSubscriber $theme={theme}>
      {children}
      <IconButton
        type="button"
        onClick={onDelete}
        mode={mode}
        color={theme.interaction.onElevation.hover}
        variant="clickable">
        <TrashIcon color={theme.text.placeholder} />
      </IconButton>
    </StyledSubscriber>
  )
}

const StyledLoadingInput = styled.div<{ $theme: SystemTheme }>`
  width: 100%;
  color: ${({ $theme }) => $theme.text.placeholder};
  border: ${({ $theme }) => `1px solid ${$theme.interaction.border.default}`};
  border-radius: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
`

const LoadingInput = () => {
  const theme = useSysTheme()
  const { t } = useTranslation()
  return (
    <StyledLoadingInput $theme={theme}>
      <SpinningQ />
      <div>{t('loadingThreeDot')}</div>
    </StyledLoadingInput>
  )
}

const MAX_SUBSCRIBER_COUNT = 25

const Subscribers = ({
  defaultSubscribers,
  isLoading
}: {
  defaultSubscribers: ScheduledReportSuggestedUser[]
  isLoading?: boolean
}) => {
  const mode = useDisplayTheme()
  const theme = useSysTheme()
  const { t } = useTranslation()
  const [subscribers, setSubscribers] = useState(defaultSubscribers)
  const suggestedUserList = useAppSelector((state) =>
    selectScheduledReportSuggestedUserList(state, subscribers)
  )
  const autocompleteRef = useRef<{ reset: () => void; value: string }>(null)
  const { setValue } = useFormContext()
  const isMaxSubscribers = subscribers.length >= MAX_SUBSCRIBER_COUNT

  useEffect(() => {
    setValue(
      'subscriptions',
      subscribers.map((subscriber) => ({ subscriberId: subscriber.id }))
    )
  }, [subscribers, setValue])

  const handleAutocompleteSelect = (
    _: React.ChangeEvent<HTMLInputElement>,
    option: ScheduledReportSuggestedUser
  ) => {
    setSubscribers([...subscribers, option])
    autocompleteRef.current?.reset()
  }

  const handleAddUser = (val: string) => {
    const targetUser = suggestedUserList.find((user) => user.email === val)
    if (targetUser) {
      setSubscribers([...subscribers, targetUser])
    }
    autocompleteRef.current?.reset()
  }

  const handleDelete = (user: ScheduledReportSuggestedUser) => {
    setSubscribers(subscribers.filter((subscriber) => subscriber.id !== user.id))
  }

  return (
    <div>
      <Tooltip
        mode={mode}
        enabled={isMaxSubscribers}
        content={t('subscriberAutoCompleteDisableTooltip', { amount: MAX_SUBSCRIBER_COUNT })}>
        <TextFieldContainer>
          {isLoading ? (
            <LoadingInput />
          ) : (
            <Autocomplete
              mode={mode}
              options={suggestedUserList}
              filterOptionsConfig={{
                sortAlphabetically: true,
                limit: 10
              }}
              ref={autocompleteRef}
              onChange={handleAutocompleteSelect}
              onEnterFreeValue={(_: React.ChangeEvent<HTMLInputElement>, val: string) =>
                handleAddUser(val)
              }
              textFieldProps={{
                width: '100%',
                placeholder: t('subscriberAutoCompletePlaceholder'),
                disabled: isMaxSubscribers
              }}
            />
          )}

          <Button
            mode={mode}
            color="secondary"
            disabled={isMaxSubscribers || isLoading}
            style={{ flex: 'none' }}
            onClick={() => handleAddUser(autocompleteRef!.current!.value)}>
            {t('add')}
          </Button>
        </TextFieldContainer>
      </Tooltip>

      <SubscriberList $theme={theme} $isLoading={isLoading} $mode={mode}>
        {isLoading ? (
          <SpinningQ />
        ) : (
          <>
            {subscribers.map((user) => (
              <Subscriber key={user.id} onDelete={() => handleDelete(user)}>
                {user.label}
              </Subscriber>
            ))}
          </>
        )}
      </SubscriberList>
    </div>
  )
}

export default Subscribers
